import { CompositeDataSetMergeFilteringRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetMergeResumeParsingRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { z } from "zod";
import { useCallback } from "react";
import { useMemo } from "react";
import { useState } from "react";
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import React from "react";
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { SelectiveFilterStringBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { SelectiveFilterDateBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';

const MergeFilteringConfigStep = z.enum([
  'initial-config',
  'job-interview-stage-filtering',
  'final-review',
  'complete',
]);

type MergeFilteringConfigStep = z.infer<typeof MergeFilteringConfigStep>;

export const DataSetCompositeRecipeMergeFilteringConfigComponent = ({
  organizationId,
  databaseId,
  config,
  setConfig,
}: {
  organizationId: string;
  databaseId: string;
  config: CompositeDataSetMergeFilteringRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
}) => {
  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<MergeFilteringConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'initial-config', display: 'Initial Config' },
        { value: 'job-interview-stage-filtering', display: 'Job Interview Stage Filtering' },
        { value: 'final-review', display: 'Final Review' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<MergeFilteringConfigStep>({
      items: stepItems,
      onNext: () => true,
    });

    const setFilteringConfig = useCallback(
    (configs: CompositeDataSetMergeFilteringRecipe['applicationCurrentStageFilteringConfigs']) => {
      setConfig({
        ...config,
        applicationCurrentStageFilteringConfigs: configs,
      });
    },
    [config, setConfig],
  );

  const filteringConfigOps = useCrudOperations({
    items: config.applicationCurrentStageFilteringConfigs ?? [],
    setItems: setFilteringConfig,
  });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'initial-config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <WorkflowDataSetSelector
              title={'Merge Dataset'}
              dataSetId={config.mergeDataSetId}
              setDataSetId={(id: string) => {
                setConfig({
                  ...config,
                  mergeDataSetId: id ? id : '',
                });
              }}
              listDataSetsPattern={listDataSetsPattern}
              filterFn={isMergeAtsIntegrationDataSet}
            />

            {/* <WorkflowDataSetSelector
              title={'Employee Candidate Filter'}
              dataSetId={config.employeeCandidatesFilterDataSetId}
              setDataSetId={(id: string) => {
                setConfig({
                  ...config,
                  employeeCandidatesFilterDataSetId: id,
                });
              }}
              listDataSetsPattern={listDataSetsPattern}
            /> */}
          </Spacing>
        );
      case 'job-interview-stage-filtering':
        return (
          <Spacing size={'sm'} direction={'vertical'}>
            <Button
              size={'small'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  filteringConfigOps.onAdd({
                    jobTitleRule: {
                        type: 'equals',
                        value: '',
                    },
                    applicationStageRule: {
                        type: 'equals',
                        value: '',
                    },
                  });
                },
              }}
              variant={'primary'}
              text={'Add'}
            />

            <Table
              items={config.applicationCurrentStageFilteringConfigs ?? []}
              columnDefinitions={[
                {
                  id: 'job',
                  header: () => <>Job Title Rule</>,
                  cell: ({ item, rowIndex }) => {
                    return (
                      <SelectiveFilterStringBasedOptionComponent
                        option={item.jobTitleRule}
                        setOption={(newValue) => {
                          filteringConfigOps.onEdit(rowIndex, {
                            ...item,
                            jobTitleRule: newValue,
                          });
                        }}
                      />
                    );
                  },
                },
                {
                    id: 'stage',
                    header: () => <>Application Stage Rule</>,
                    cell: ({ item, rowIndex }) => {
                        return (
                        <SelectiveFilterStringBasedOptionComponent
                            option={item.applicationStageRule}
                            setOption={(newValue) => {
                            filteringConfigOps.onEdit(rowIndex, {
                                ...item,
                                applicationStageRule: newValue,
                            });
                            }}
                        />
                        );
                    },
                },
                {
                  id: 'delete',
                  header: () => <></>,
                  cell: ({ rowIndex }) => {
                    return (
                      <Box
                        as={'div'}
                        display={'flex'}
                        flexDirection={'col'}
                        alignItems={'center'}
                      >
                        <Button
                          behavior={{
                            type: 'onClick',
                            onClick: () => {
                              filteringConfigOps.onDelete(rowIndex);
                            },
                          }}
                          variant={'destructive'}
                          size={'small'}
                          text={'Delete'}
                        />
                      </Box>
                    );
                  },
                },
              ]}
            />
          </Spacing>
        );
      case 'final-review':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <div></div>
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [
    config,
    dataWizardPayload.selectedItem,
    listDataSetsPattern,
    setConfig,
    filteringConfigOps,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
